import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, Download } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

type MediaItem = {
  id: number;
  type: 'image' | 'video' | 'news';
  title: string;
  src: string;
  date?: string;
  description?: string;
};

const Gallery = () => {
  const [selectedTab, setSelectedTab] = useState<'images' | 'videos' | 'news'>('images');
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Sample gallery data
  const galleryData = {
    images: Array.from({ length: 9 }, (_, i) => ({
      id: i + 1,
      type: 'image' as const,
      title: `Event ${i + 1}`,
      src: `/jeevan-jyoti-ngo/images/gallery/img${i + 1}.jpg`,
      date: '2023-09-01',
      description: `Description for event ${i + 1}`,
    })),
    videos: Array.from({ length: 4 }, (_, i) => ({
      id: i + 1,
      type: 'video' as const,
      title: `Video ${i + 1}`,
      src: `https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1`,
      date: '2023-08-15',
      description: `Description for video ${i + 1}`,
    })),
    news: Array.from({ length: 6 }, (_, i) => ({
      id: i + 1,
      type: 'news' as const,
      title: `News Update ${i + 1}`,
      src: `/jeevan-jyoti-ngo/images/news/news${(i % 3) + 1}.jpg`,
      date: '2023-09-01',
      description: `Brief summary of news item ${i + 1} with more details about the event or update.`,
    })),
  };

  const openMedia = (media: MediaItem) => {
    setSelectedMedia(media);
    setZoomLevel(1);
    document.body.style.overflow = 'hidden';
  };

  const closeMedia = () => {
    setSelectedMedia(null);
    document.body.style.overflow = 'auto';
    setIsFullscreen(false);
  };

  const navigateMedia = (direction: 'prev' | 'next') => {
    if (!selectedMedia) return;
    
    const currentItems = galleryData[selectedTab];
    const currentIndex = currentItems.findIndex(item => item.id === selectedMedia.id);
    
    if (direction === 'prev') {
      const prevIndex = (currentIndex - 1 + currentItems.length) % currentItems.length;
      setSelectedMedia(currentItems[prevIndex]);
    } else {
      const nextIndex = (currentIndex + 1) % currentItems.length;
      setSelectedMedia(currentItems[nextIndex]);
    }
    setZoomLevel(1);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(console.log);
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(console.log);
        setIsFullscreen(false);
      }
    }
  };

  const zoomIn = () => setZoomLevel(prev => Math.min(prev + 0.25, 3));
  const zoomOut = () => setZoomLevel(prev => Math.max(prev - 0.25, 0.5));

  const downloadMedia = () => {
    if (!selectedMedia) return;
    
    const link = document.createElement('a');
    link.href = selectedMedia.src;
    link.download = `${selectedMedia.title.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderMediaItem = (item: MediaItem) => (
    <motion.div
      key={`${selectedTab}-${item.id}`}
      className="group relative overflow-hidden rounded-xl shadow-lg cursor-pointer bg-white"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onClick={() => openMedia(item)}
    >
      {item.type === 'image' || item.type === 'news' ? (
        <>
          <img
            src={item.src}
            alt={item.title}
            className="w-full h-64 object-cover transform group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              (e.target as HTMLImageElement).src = `https://picsum.photos/600/400?random=${item.id}`;
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
            <h3 className="text-white font-semibold text-lg">{item.title}</h3>
            <p className="text-gray-200 text-sm">{item.date}</p>
          </div>
        </>
      ) : (
        <div className="relative">
          <div className="w-full h-64 bg-gray-800 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-red-600 bg-opacity-80 flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
          <div className="p-4">
            <h3 className="font-semibold text-gray-900">{item.title}</h3>
            <p className="text-sm text-gray-500">{item.date}</p>
          </div>
        </div>
      )}
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-grow pt-24">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-blue-900 to-purple-900 text-white py-20 -mt-24">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0 bg-[url('/images/pattern.png')] bg-cover"></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center pt-16">
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Our Gallery
            </motion.h1>
            <motion.p 
              className="text-xl text-blue-100 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Explore our collection of images, videos, and news updates
            </motion.p>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="flex justify-center">
            <div className="inline-flex rounded-lg bg-white p-1 shadow-md">
              {[
                { id: 'images', label: 'Images' },
                { id: 'videos', label: 'Videos' },
                { id: 'news', label: 'News' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  className={`px-6 py-3 rounded-md text-sm font-medium transition-colors ${
                    selectedTab === tab.id
                      ? 'bg-[#da291c] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setSelectedTab(tab.id as any)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryData[selectedTab].map(renderMediaItem)}
          </div>
        </div>

        {/* Media Modal */}
        <AnimatePresence>
          {selectedMedia && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMedia}
            >
              {/* Close Button */}
              <button
                className="absolute top-4 right-4 text-white p-2 hover:bg-white hover:bg-opacity-20 rounded-full"
                onClick={closeMedia}
                aria-label="Close media viewer"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Navigation Buttons */}
              <button
                className="absolute left-4 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateMedia('prev');
                }}
                aria-label="Previous media"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Media Content */}
              <div 
                className="relative max-w-full max-h-[90vh] overflow-auto flex items-center justify-center"
                onClick={e => e.stopPropagation()}
              >
                {selectedMedia.type === 'image' || selectedMedia.type === 'news' ? (
                  <motion.div
                    className="relative"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      transform: `scale(${zoomLevel})`,
                      transformOrigin: 'center',
                    }}
                  >
                    <img
                      src={selectedMedia.src}
                      alt={selectedMedia.title}
                      className="max-w-full max-h-[80vh] object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://picsum.photos/1200/800?random=${selectedMedia?.id}`;
                      }}
                    />
                  </motion.div>
                ) : (
                  <div className="w-full max-w-4xl aspect-video bg-black">
                    <iframe
                      width="100%"
                      height="100%"
                      src={selectedMedia.src}
                      title={selectedMedia.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    ></iframe>
                  </div>
                )}
              </div>

              <button
                className="absolute right-4 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateMedia('next');
                }}
                aria-label="Next media"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Media Controls */}
              {(selectedMedia.type === 'image' || selectedMedia.type === 'news') && (
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 rounded-full px-4 py-2 flex items-center space-x-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      zoomOut();
                    }}
                    className="text-white p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
                    aria-label="Zoom out"
                  >
                    <ZoomOut className="w-5 h-5" />
                  </button>
                  <span className="text-white text-sm">{Math.round(zoomLevel * 100)}%</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      zoomIn();
                    }}
                    className="text-white p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
                    aria-label="Zoom in"
                  >
                    <ZoomIn className="w-5 h-5" />
                  </button>
                  <div className="h-6 w-px bg-gray-500 mx-1"></div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFullscreen();
                    }}
                    className="text-white p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
                    aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  >
                    <Maximize2 className="w-5 h-5" />
                  </button>
                  <div className="h-6 w-px bg-gray-500 mx-1"></div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadMedia();
                    }}
                    className="text-white p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
                    aria-label="Download"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                </div>
              )}

              {/* Media Info */}
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <div className="inline-flex items-center bg-black bg-opacity-50 text-white px-4 py-1 rounded-full text-sm">
                  <span>
                    {galleryData[selectedTab].findIndex(item => item.id === selectedMedia.id) + 1} / {galleryData[selectedTab].length}
                  </span>
                  {(selectedMedia.title || selectedMedia.description) && (
                    <div className="ml-3 text-left max-w-xs truncate">
                      {selectedMedia.title && <div className="font-medium">{selectedMedia.title}</div>}
                      {selectedMedia.description && (
                        <div className="text-xs text-gray-300 truncate">{selectedMedia.description}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  );
};

export default Gallery;
